import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export interface PlayerSettings {
  crossfadeDuration: number
  replayGainEnabled: boolean
  replayGainPreampDb: number
  radioMode: boolean
  radioFollow: boolean
  beatPulse: boolean
  beatPulseOpacity: number
  desktopNotifications: boolean
  interstitialsEnabled: boolean
  interstitialMinMinutes: number
  interstitialMaxMinutes: number
}

export interface MoodTargets {
  danceability: number | null
  aggression: number | null
  happiness: number | null
  party: number | null
  relaxation: number | null
  sadness: number | null
  energy: number | null
}

export const DEFAULT_MOOD_TARGETS: MoodTargets = {
  danceability: null,
  aggression: null,
  happiness: null,
  party: null,
  relaxation: null,
  sadness: null,
  energy: null,
}

export interface RadioSettings {
  harmonicWeight: number
  bpmWeight: number
  artistWeight: number
  embeddingWeight: number
  moodTargets: MoodTargets
}

export const DEFAULT_RADIO_SETTINGS: RadioSettings = {
  harmonicWeight: 2,
  bpmWeight: 1,
  artistWeight: 3,
  embeddingWeight: 0.5,
  moodTargets: DEFAULT_MOOD_TARGETS,
}

// Session-scoped adaptive-radio feedback. Not persisted — taste should reset
// each session rather than carry stale signals across days.
export interface RadioFeedback {
  likeIds: number[]
  dislikeIds: number[]
}

export const RADIO_FEEDBACK_CAP = 8

const DEFAULT_RADIO_FEEDBACK: RadioFeedback = { likeIds: [], dislikeIds: [] }

export interface AppState {
  playerSettings: PlayerSettings
  setPlayerSettings: (patch: Partial<PlayerSettings>) => void
  radioSettings: RadioSettings
  setRadioSettings: (patch: Partial<RadioSettings>) => void
  setMoodTarget: (key: keyof MoodTargets, value: number | null) => void
  radioFeedback: RadioFeedback
  recordRadioLike: (trackId: number) => void
  recordRadioDislike: (trackId: number) => void
  resetRadioFeedback: () => void
  // Seed track id radio dead-ended on (no similar candidates left), or null. Drives
  // the recovery modal; transient (not persisted).
  radioDeadEndSeedId: number | null
  setRadioDeadEnd: (seedId: number | null) => void
  themeId: string
  setThemeId: (id: string) => void
  controlsDrawerOpen: boolean
  setControlsDrawerOpen: (open: boolean) => void
}

// Add `id` to the front of `list` (most-recent first), drop it from `other`,
// dedupe, and cap to the most recent RADIO_FEEDBACK_CAP entries.
function pushFeedback(list: number[], other: number[], id: number): [number[], number[]] {
  return [
    [id, ...list.filter((x) => x !== id)].slice(0, RADIO_FEEDBACK_CAP),
    other.filter((x) => x !== id),
  ]
}

/** The previous preamp default, reset by the v1 migration below. */
const LEGACY_PREAMP_DEFAULT_DB = 6

const defaultPlayerSettings: PlayerSettings = {
  crossfadeDuration: 3,
  replayGainEnabled: true,
  // A positive preamp is eaten unevenly by the peak clamp in replayGainToLinear:
  // at +6 dB roughly a third of the library clips against its own peak and lands
  // somewhere other than the -18 LUFS target, reintroducing the very variance
  // ReplayGain removes. Raise the volume slider instead.
  replayGainPreampDb: 0,
  radioMode: false,
  radioFollow: false,
  beatPulse: false,
  beatPulseOpacity: 0.15,
  desktopNotifications: false,
  interstitialsEnabled: false,
  interstitialMinMinutes: 30,
  interstitialMaxMinutes: 40,
}

const transientKeys: Array<keyof AppState> = [
  'setPlayerSettings',
  'setRadioSettings',
  'setMoodTarget',
  'radioFeedback',
  'recordRadioLike',
  'recordRadioDislike',
  'resetRadioFeedback',
  'radioDeadEndSeedId',
  'setRadioDeadEnd',
  'setThemeId',
  'setControlsDrawerOpen',
]
const appStateKey = 'hypersonic-app-state'

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      playerSettings: defaultPlayerSettings,
      setPlayerSettings: (patch) =>
        set((s) => ({ playerSettings: { ...s.playerSettings, ...patch } })),
      radioSettings: DEFAULT_RADIO_SETTINGS,
      setRadioSettings: (patch) =>
        set((s) => ({ radioSettings: { ...s.radioSettings, ...patch } })),
      setMoodTarget: (key, value) =>
        set((s) => ({
          radioSettings: {
            ...s.radioSettings,
            moodTargets: { ...s.radioSettings.moodTargets, [key]: value },
          },
        })),
      radioFeedback: DEFAULT_RADIO_FEEDBACK,
      recordRadioLike: (trackId) =>
        set((s) => {
          const [likeIds, dislikeIds] = pushFeedback(
            s.radioFeedback.likeIds,
            s.radioFeedback.dislikeIds,
            trackId
          )
          return { radioFeedback: { likeIds, dislikeIds } }
        }),
      recordRadioDislike: (trackId) =>
        set((s) => {
          const [dislikeIds, likeIds] = pushFeedback(
            s.radioFeedback.dislikeIds,
            s.radioFeedback.likeIds,
            trackId
          )
          return { radioFeedback: { likeIds, dislikeIds } }
        }),
      resetRadioFeedback: () => set({ radioFeedback: DEFAULT_RADIO_FEEDBACK }),
      radioDeadEndSeedId: null,
      setRadioDeadEnd: (seedId) => set({ radioDeadEndSeedId: seedId }),
      themeId: 'hypersonic',
      setThemeId: (id) => set({ themeId: id }),
      controlsDrawerOpen: false,
      setControlsDrawerOpen: (open) => set({ controlsDrawerOpen: open }),
    }),
    {
      name: appStateKey,
      version: 1,
      storage: createJSONStorage(() => localStorage),
      // Existing installs persisted the old +6 preamp, so the new default would
      // never reach them. Only the untouched old default is reset — a preamp the
      // user actually dialled in is left alone.
      migrate: (persisted, version) => {
        const p = persisted as Partial<AppState>
        if (version === 0 && p.playerSettings?.replayGainPreampDb === LEGACY_PREAMP_DEFAULT_DB) {
          return {
            ...p,
            playerSettings: {
              ...p.playerSettings,
              replayGainPreampDb: defaultPlayerSettings.replayGainPreampDb,
            },
          }
        }
        return p
      },
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => !transientKeys.includes(key as keyof AppState))
        ),
      merge: (persisted, current) => {
        const p = persisted as Partial<AppState>
        return {
          ...current,
          ...p,
          playerSettings: { ...current.playerSettings, ...p.playerSettings },
          radioSettings: {
            ...current.radioSettings,
            ...p.radioSettings,
            moodTargets: { ...current.radioSettings.moodTargets, ...p.radioSettings?.moodTargets },
          },
        }
      },
    }
  )
)
